// Externals dependencies.
import { LitElement, html, css, unsafeCSS } from "lit";
import { __ } from "@wordpress/i18n";

// Internal dependencies.
import { sharedStyles } from "./styles/shared-styles";
import "./app-nav";

/**
 * AppHeader component class.
 *
 * @since 0.1.0
 * @class AppHeader
 * @extends {LitElement}
 */
class AppHeader extends LitElement {
    /**
     * Creates an instance of AppHeader.
     * 
     * @since 0.1.0
     * @memberof AppHeader
     */
    constructor() {
        super();
        this.isNavOpen = false;
        this.hasBeenScrolled = false;
        this.removeTop = false;
        this.noticeRepeatCount = 1;
        this.noticeAnimationDuration = 1;
        this.noticeResizeObserver = null;
        this.headerResizeObserver = null;
        this.toggleNav = this.toggleNav.bind( this );
        this.handleWindowResize = this.handleWindowResize.bind( this );
        this.handleWindowScroll = this.handleWindowScroll.bind( this );
    }

    /**
     * Inits the component's properties.
     *
     * @since 0.1.0
     * @since 0.15.0 Added the properties: cartContentsCount.
     * @readonly
     * @static
     * @memberof AppHeader
     */
    static get properties() {
        return {
            name: { type: String },
            logoUrl: { type: String },
            accountUrl: { type: String },
            cartUrl: { type: String },
            cartContentsCount: { type: String },
            headerNotice: { type: String, attribute: "header-notice" },
            noticeRepeatCount: { state: true },
            noticeAnimationDuration: { state: true },
            isNavOpen: { type: Boolean },
            hasBeenScrolled: { type: Boolean },
            removeTop: { type: Boolean },
        };
    }

    /**
     * Inits the component's CSS styles.
     *
     * @since 0.1.0
     * @readonly
     * @static
     * @memberof AppHeader
     */
    static get styles() {
        const headerTop = AppHeader.headerTop();

        return [ sharedStyles, css`
            .ts-header {
                z-index: 1030;
                position: fixed;
                top: ${unsafeCSS( headerTop )}px;
                right: 0;
                left: 0;
                background-color: transparent;
                transition: background-color .1s ease-in-out, top .2s linear;
            }

            .ts-header--bg-white {
                background-color: white !important;
                box-shadow: 0 .5rem 1rem rgba(0, 0, 0, .15);
            }

            .ts-header--no-top {
                top: 0 !important;
            }

            .ts-header-top {
                padding: .25rem;
                text-align: center;
                text-transform: uppercase;
                font-weight: 700;
                font-size: 80%;
                color: white;
                background-color: var(--ts-color-primary);
            }

            .ts-header-top-content {
                width: 100%;
                overflow: hidden;
            }

            .ts-header-top-track {
                display: flex;
                width: max-content;
                animation: ts-header-marquee var(--ts-header-marquee-duration) linear infinite;
            }

            .ts-header-top-group {
                display: flex;
                flex: 0 0 auto;
            }

            .ts-header-top-text {
                display: inline-block;
                flex: 0 0 auto;
                padding: 0 0.2rem;
                white-space: nowrap;
            }

            .ts-header-top-accessible {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }

            @keyframes ts-header-marquee {
                to { transform: translateX(-50%); }
            }

            @media (prefers-reduced-motion: reduce) {
                .ts-header-top-track {
                    display: block;
                    width: 100%;
                    animation: none;
                    transform: none;
                }

                .ts-header-top-group {
                    display: block;
                    width: 100%;
                    text-align: center;
                }

                .ts-header-top-group[aria-hidden="true"] {
                    display: none;
                }

                .ts-header-top-text {
                    display: none;
                    padding: 0;
                    white-space: normal;
                }

                .ts-header-top-text:first-child {
                    display: inline;
                }
            }

            .ts-header-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1rem 15px;
                transition: padding .4s linear;
            }

            .ts-header--small .ts-header-container {
                padding: .5rem 15px;
            }

            .ts-header-logo {
                display: block;
                max-width: 5.875rem;
                height: auto;
                transition: max-width .4s ease-in-out;
            }

            .ts-header-logo--small {
                max-width: 50% !important;
            }

            .ts-header-icon-list {
                z-index: 1032;
                position: relative;
                list-style: none;
                padding: 0;
                margin: 0;
                display: flex;
                align-items: center;
                margin: -30% auto 0px;
            }

            .ts-header-icon-list__item:not(:last-child) {
                margin-right: 1rem;
            }

            .ts-header-icon {
                display: flex;
                padding: .5rem;
                background-color: white;
                border-radius: 50%;
            }

            .ts-header-icon__image {
                width: 1.125rem;
                height: 1.125rem;
            }

            .ts-header-icon svg .burger1,
            .ts-header-icon svg .burger3 {
                transform: matrix(1, 0, 0, 1, 0, 0);
            }

            .ts-header-icon svg .burger2 {
                opacity: 1;
            }

            .ts-header-icon svg .burger1,
            .ts-header-icon svg .burger2,
            .ts-header-icon svg .burger3 {
                transition: transform .6s ease-in-out;
            }
            
            .ts-header-icon.ts-header-icon--animate svg .burger1,
            .ts-header-icon.ts-header-icon--animate svg .burger3 {
                transform-origin: 0px 0px 0px;
            }

            .ts-header-icon.ts-header-icon--animate svg .burger1 {
                transform: matrix(0.7071, 0.7071, -0.7071, 0.7071, 130.797, 24.2284);
            }

            .ts-header-icon.ts-header-icon--animate svg .burger2 {
                opacity: 0;
            }

            .ts-header-icon.ts-header-icon--animate svg .burger3 {
                transform: matrix(0.7071, -0.7071, 0.7071, 0.7071, -293.467, 151.508);
            }

            .ts-header-cart-contents-count {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 1.6rem;
                height: 1.6rem;
                color: white;
                background-color: red;
                border-radius: 50%;
                font-family: var(--font-lilita);
                font-size: 80%;
                text-align: center;
                position: absolute;
                right: -.6rem;
                bottom: -.6rem;
            }

            
            .ts-header--small .ts-header-icon-list {
                margin: 0 auto 0px;
            }

            @media (min-width: 576px) {
                .ts-header-top {
                    font-size: 90%;
                }
            }

            @media (min-width: 768px) {
                .ts-header-icon {
                    padding: 1rem;
                }

                .ts-header-icon__image {
                    width: 1.563rem;
                    height: 1.563rem;
                }

                .ts-header-cart-contents-count {
                    right: -.2rem;
                    bottom: -.2rem;
                }
            }

            @media (min-width: 992px) {
                .ts-header-container {
                    padding: 3rem calc(15px + 1rem)
                }
            }

            @media (max-width: 767px) {
                .ts-header-icon-list {
                    margin: -8% auto 0px;
                }
            }
        ` ];
    }

    /**
     * Returns the top CSS property for the .ts-header element.
     *
     * @static
     * @returns Number headerTop.
     * @memberof AppHeader
     */
    static headerTop() {
        let headerTop = 0;

        if ( "1" === window.app.is_admin_bar_showing ) {
            headerTop = ( window.matchMedia( "(min-width: 783px)" ).matches ) ? 32 : 46;
        }

        return headerTop;
    }

    /**
     * Runs when the component is appended to the document.
     *
     * @since 0.1.0
     * @listens event:resize
     * @memberof AppHeader
     */
    connectedCallback() {
        super.connectedCallback();
        this.noticeResizeObserver = new window.ResizeObserver( () => this.updateNoticeMarquee() );
        this.noticeResizeObserver.observe( this );
        window.addEventListener( 'resize', this.handleWindowResize );
        window.addEventListener( 'scroll', this.handleWindowScroll );

        if ( "1" === window.app.is_header_small ) {
            this.hasBeenScrolled = true;
        }

        this.updateComplete.then( () => {
            if ( this.isConnected ) {
                this.observeHeaderSize();
            }
        } );
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener( 'resize', this.handleWindowResize );
        window.removeEventListener( 'scroll', this.handleWindowScroll );
        this.noticeResizeObserver?.disconnect();
        this.noticeResizeObserver = null;
        this.headerResizeObserver?.disconnect();
    }

    handleWindowResize() {
        const header = this.shadowRoot.querySelector( '.ts-header' );

        if ( header ) {
            const headerTop = AppHeader.headerTop();
            header.style.top = `${headerTop}px`;
        }

        this.updateHeaderHeight();
        this.updateNoticeMarquee();
    }

    handleWindowScroll() {
        const currentScrollPosition = window.pageYOffset;

        if ( window.app.is_admin_bar_showing && window.matchMedia( "(max-width: 600px)" ).matches ) {
            this.removeTop = ( 1 <= currentScrollPosition ) ? true : false;
        }

        if ( "1" !== window.app.is_header_small ) {
            this.hasBeenScrolled = ( 8 < currentScrollPosition ) ? true : false;
        }

        this.updateHeaderHeight();
    }

    observeHeaderSize() {
        const header = this.shadowRoot.querySelector( '.ts-header' );

        if ( ! header ) {
            return;
        }

        if ( ! this.headerResizeObserver ) {
            this.headerResizeObserver = new window.ResizeObserver( () => this.updateHeaderHeight() );
        }

        this.headerResizeObserver.observe( header );
        this.updateHeaderHeight();
    }

    updateHeaderHeight() {
        const header = this.shadowRoot.querySelector( '.ts-header' );

        if ( ! header ) {
            return;
        }

        const headerHeight = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
            '--ts-header-height',
            `calc(${headerHeight}px + var(--wp-admin--admin-bar--height, 0px))`
        );
    }

    firstUpdated() {
        this.updateNoticeMarquee();
    }

    updated( changedProperties ) {
        if ( changedProperties.has( 'headerNotice' ) ) {
            this.updateNoticeMarquee();
        }
    }

    updateNoticeMarquee() {
        const viewport = this.shadowRoot.querySelector( '.ts-header-top-content' );
        const sample = this.shadowRoot.querySelector( '.ts-header-top-text' );

        if ( ! viewport || ! sample ) {
            return;
        }

        const textWidth = sample.getBoundingClientRect().width;
        const viewportWidth = viewport.clientWidth;

        if ( ! textWidth || ! viewportWidth ) {
            return;
        }

        const repeatCount = Math.ceil( viewportWidth / textWidth ) + 1;
        const animationDuration = ( textWidth * repeatCount ) / 50;

        if ( repeatCount !== this.noticeRepeatCount || animationDuration !== this.noticeAnimationDuration ) {
            this.noticeRepeatCount = repeatCount;
            this.noticeAnimationDuration = animationDuration;
        }
    }

    /**
     * Toggles the nav menu.
     *
     * @since 0.1.0
     * @param object e
     * @memberof AppHeader
     */
    toggleNav( e ) {
        e.preventDefault();
        this.isNavOpen = !this.isNavOpen;
        const overflowValue = ( this.isNavOpen ) ? 'hidden' : 'visible';
        document.body.style.overflow = overflowValue;
    }

    /**
     * Renders the component.
     *
     * @since 0.1.0
     * @returns TemplateResult
     * @memberof AppHeader
     */
    render() {
        const notice = this.headerNotice?.trim();
        const noticeCopies = Array.from( { length: this.noticeRepeatCount }, () => html`
            <span class="ts-header-top-text">${notice}</span>
        ` );

        return html`
            <header class="ts-header${this.hasBeenScrolled ? ' ts-header--bg-white ts-header--small' : ''}${this.removeTop ? ' ts-header--no-top' : ''}">
                ${notice ? html`
                    <div class="ts-header-top">
                        <span class="ts-header-top-accessible">${notice}</span>
                        <div class="ts-header-top-content">
                            <div class="ts-header-top-track" style="--ts-header-marquee-duration: ${this.noticeAnimationDuration}s" aria-hidden="true">
                                <div class="ts-header-top-group">${noticeCopies}</div>
                                <div class="ts-header-top-group" aria-hidden="true">${noticeCopies}</div>
                            </div>
                        </div>
                    </div>
                ` : ''}
                <div class="ts-header-container">
                    <div>
                        <a href="${window.app.site_url}" class="d-block" title="${window.app.name}">
                            ${this.logoUrl ? html`<img src="${this.logoUrl}" alt="${this.name}" class="ts-header-logo${this.hasBeenScrolled ? ' ts-header-logo--small' : ''}">` : html`<span class="d-block">${this.name}</span>`}
                        </a>
                    </div>
                    <div>
                        <ul class="ts-header-icon-list">
                            <li class="ts-header-icon-list__item">
                                <a href="${this.accountUrl}" title="${__( 'Mi Cuenta', 'tio-simon-foods' )}" class="ts-header-icon">
                                    <img src="${window.app.theme_file_uri}/assets/images/icon-account.svg" class="ts-header-icon__image">
                                </a>
                            </li>
                            <li class="ts-header-icon-list__item">
                                <a href="${this.cartUrl}" title="${__( 'Carrito', 'tio-simon-foods' )}" class="ts-header-icon position-relative">
                                    <img src="${window.app.theme_file_uri}/assets/images/icon-cart.svg" class="ts-header-icon__image">
                                    <span class="ts-header-cart-contents-count">${this.cartContentsCount}</span>
                                </a>
                            </li>
                            <li class="ts-header-icon-list__item">
                                <a href="#" class="ts-header-icon${this.isNavOpen ? ' ts-header-icon--animate' : ''}" @click="${this.toggleNav}">
                                    <svg class="ts-header-icon__image" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 700 600">
                                        <rect id="burger1" class="burger1" rx="40" ry="40" width="700" height="80" fill="#535152"></rect>
                                        <rect id="burger2" class="burger2" y="260" rx="40" ry="40" width="700" height="80" fill="#535152"></rect>
                                        <rect id="burger3" class="burger3" y="520" rx="40" ry="40" width="700" height="80" fill="#535152"></rect>
                                    </svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <app-nav ?isOpen="${this.isNavOpen}"></app-nav>
            </header>
        `;
    }
}

window.customElements.define( 'app-header', AppHeader );
